import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { DialogShell } from './dialog';
import { useDocumentVisible } from './document-visible';
import { BUTTON_PRIMARY, BUTTON_SECONDARY } from './ui';

/**
 * Noticing that a new build has been deployed under an open tab.
 *
 * Both products are a single-page app people leave open for days, so a deploy
 * reaches nobody until they happen to reload. Production syncs without
 * `--delete`, so the old build's chunks stay in the bucket and a stale tab
 * keeps working — which is the right trade, and also the reason nothing ever
 * forces the issue. Staging does delete, and there the first lazy route a
 * stale tab opens 404s.
 *
 * So there are two halves here, and both are needed:
 *
 *   - `useNewBuildAvailable` polls for a deploy. Once there is one, the next
 *     move to another screen loads it (`useReloadOnNavigation`), and only a
 *     tab that stays on one screen for hours is shown the banner. Never a
 *     reload on a timer: the estimator holds a navigation with `useBlocker`
 *     while a debounced save drains, and a reload underneath that loses the
 *     last few edits.
 *   - `useStaleChunkFailure` catches the case where the tab has already
 *     broken — a chunk that is no longer in the bucket — and says so, instead
 *     of leaving the white screen a failed dynamic import otherwise gives.
 *
 * Nothing here talks to the API. `index.html` is deployed with
 * `Cache-Control: no-cache` and every deploy invalidates the distribution, so
 * a fetch of it is always the live one, and the module script it names is
 * content-hashed — which makes that filename the identity of the build. See
 * `.github/workflows/deploy.yml`.
 */

/** Five minutes. A deploy nobody hears about for five minutes has cost nothing. */
export const NEW_BUILD_POLL_MS = 300_000;

/** How long a tab that has not changed screen runs the old build before it is asked to reload. */
export const NEW_BUILD_BANNER_AFTER_MS = 2 * 60 * 60_000;

/** How long "Not now" lasts, for every tab of the app at once. */
export const NEW_BUILD_SNOOZE_MS = 4 * 60 * 60_000;

const SNOOZE_KEY = 'iqd:new-build-snoozed-until';

/**
 * The entry chunk a document boots from.
 *
 * The first module script rather than the last, with the development server's
 * own client skipped: a built `index.html` names exactly one — the preloads
 * beside it are `<link>` tags — and anything a running app injects later
 * lands after it. So this is the same answer read from the live document and
 * from the HTML fetched below, which is what makes the two comparable.
 */
function entryOf(doc: Document): string | null {
  const scripts = Array.from(doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'));
  const app = scripts
    .map((script) => script.getAttribute('src'))
    .find((src): src is string => src !== null && !src.startsWith('/@vite/'));
  return app ?? null;
}

/**
 * Whether the deployed build is newer than the one this tab is running.
 *
 * Latches: once it is true there is nothing further to learn, and the polling
 * stops. Only polls while the tab is being looked at — one left behind another
 * all week should not be asking — and every failure is swallowed, because
 * being briefly offline is not news about a deploy.
 */
export function useNewBuildAvailable(pollMs: number = NEW_BUILD_POLL_MS): boolean {
  const [available, setAvailable] = useState(false);

  /**
   * The entry this tab is running, read once on the way in.
   *
   * A document's own script tags do not change while it is open, so when this
   * is read does not matter — only that nothing injected since is mistaken for
   * it, which `entryOf` handles.
   *
   * In development this is the unhashed dev entry and the fetched HTML names
   * the same one, so nothing below ever fires. That is the intended behaviour
   * rather than a special case: there is no deploy to notice.
   */
  const [runningEntry] = useState(() => entryOf(document));

  const visible = useDocumentVisible();

  /**
   * When the last request went out, so that bringing this tab forward six
   * times in a minute does not send six requests. The first check, with
   * nothing recorded, is due immediately: a tab restored from a session, or
   * left open overnight, should find out on the way in.
   */
  const lastCheckAt = useRef(0);

  useEffect(() => {
    if (available || !visible || !runningEntry) return;

    const controller = new AbortController();

    const check = async () => {
      lastCheckAt.current = Date.now();
      try {
        // `no-store` for the browser's own cache; the response is `no-cache`
        // from S3 and the distribution is invalidated on every deploy, so
        // what comes back is the live entry point. No cache-busting query:
        // the distribution is not configured to forward one, so it would buy
        // nothing and cost a miss.
        const response = await fetch('/index.html', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return;

        // Parsed rather than pattern-matched: attribute order is the
        // bundler's business, and `DOMParser` runs no scripts.
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const deployed = entryOf(html);

        // A response that names no entry at all is something other than this
        // app — a captive portal, an error page from in front of the CDN —
        // and is not evidence of anything.
        if (deployed && deployed !== runningEntry) setAvailable(true);
      } catch {
        // Offline, a CDN blip, or the abort below. None of them is a deploy.
      }
    };

    let interval: ReturnType<typeof setInterval> | undefined;
    const first = setTimeout(
      () => {
        void check();
        interval = setInterval(() => void check(), pollMs);
      },
      Math.max(0, pollMs - (Date.now() - lastCheckAt.current)),
    );

    return () => {
      clearTimeout(first);
      if (interval) clearInterval(interval);
      controller.abort();
    };
  }, [available, pollMs, runningEntry, visible]);

  return available;
}

/**
 * What a browser says when a module it asked for is not being served.
 *
 * Each engine words it differently, and Vite adds one of its own for the
 * stylesheet beside a chunk. None of them is a fault in the build: they are
 * what a tab that has been open across a deploy meets on staging, where the
 * sync deletes the old assets.
 */
const STALE_CHUNK_MESSAGES = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
];

/**
 * Whether a thrown thing is a chunk that has gone missing under an open tab.
 *
 * Message matching, because that is all there is: a failed dynamic import
 * rejects with a plain `TypeError` carrying no code, no cause and — in this
 * app, where the chunk is fetched by the browser rather than by us — no
 * response to read a status off.
 *
 * Used to keep these out of error reporting. `NewBuildNotice` has already told
 * whoever is looking to reload, so an issue per stale tab per deploy is noise
 * about a deploy rather than news about a fault.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return STALE_CHUNK_MESSAGES.some((pattern) => pattern.test(message));
}

/**
 * Whether a chunk this tab asked for is no longer there.
 *
 * Vite fires `vite:preloadError` on the window when a dynamic import cannot
 * be fetched, which is what a stale tab meets on staging after a deploy: every
 * route is lazy, so the first navigation is a 404 and an error boundary
 * showing a message about a module. Reloading is the whole answer, so this
 * exists to be able to say so.
 *
 * The event is deliberately not cancelled. Calling `preventDefault` tells Vite
 * to carry on into the import instead of throwing, which changes what the
 * screens' own error handling sees for the sake of an error the dialog above
 * it has already explained.
 */
export function useStaleChunkFailure(): boolean {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onPreloadError = () => setFailed(true);
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
  }, []);

  return failed;
}

function reload() {
  window.location.reload();
}

/** Things open that a reload would throw away, such as a half-written Ask Derek question. */
let reloadHolds = 0;

/** Keeps a navigation from loading a new build while `active`. */
export function useHoldNewBuildReload(active = true) {
  useEffect(() => {
    if (!active) return;
    reloadHolds += 1;
    return () => {
      reloadHolds -= 1;
    };
  }, [active]);
}

/** Long enough for a save fired by the same click, and Apollo's batch window, to get its request out. */
export const NEW_BUILD_RELOAD_DELAY_MS = 300;

const RELOADED_FROM_KEY = 'iqd:new-build-reloaded-from';

/**
 * The screen a path belongs to: its first two segments.
 *
 * Tabs, drill-downs and record switches inside a screen are path changes too,
 * and reloading on each would make the first one after a deploy a full boot.
 */
export function screenOf(pathname: string): string {
  return pathname.split('/').filter(Boolean).slice(0, 2).join('/');
}

let reloadIsUseless: boolean | undefined;

/**
 * Whether the last navigation reload came back to the same build.
 *
 * Read once per page load. If it did, detection is wrong about this tab, and
 * reloading on every navigation would make the app unusable.
 */
function lastReloadChangedNothing(): boolean {
  if (reloadIsUseless === undefined) {
    try {
      const from = window.sessionStorage.getItem(RELOADED_FROM_KEY);
      window.sessionStorage.removeItem(RELOADED_FROM_KEY);
      reloadIsUseless = from !== null && from === entryOf(document);
    } catch {
      reloadIsUseless = false;
    }
  }
  return reloadIsUseless;
}

/**
 * Loads the new build at the next move to another screen.
 *
 * A passive effect, so it runs after the screen being left has cleaned up and
 * the estimator's blocker has let the navigation through. `REPLACE` is skipped:
 * those are redirects and URL tidying, not someone choosing a screen.
 */
export function useReloadOnNavigation(available: boolean, onReload: () => void = reload) {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef(screenOf(pathname));
  const scheduled = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const screen = screenOf(pathname);
    const moved = screen !== previous.current;
    previous.current = screen;
    if (!moved || !available || navigationType === 'REPLACE' || scheduled.current) return;
    if (reloadHolds > 0 || lastReloadChangedNothing()) return;

    scheduled.current = setTimeout(() => {
      try {
        window.sessionStorage.setItem(RELOADED_FROM_KEY, entryOf(document) ?? '');
      } catch {
        // Without the breadcrumb the reload still happens; only the loop guard is lost.
      }
      onReload();
    }, NEW_BUILD_RELOAD_DELAY_MS);
  }, [available, navigationType, onReload, pathname]);

  useEffect(
    () => () => {
      if (scheduled.current) clearTimeout(scheduled.current);
    },
    [],
  );
}

function readSnoozedUntil(): number {
  try {
    // Capped, so a value written while the clock was wrong cannot hide the banner for good.
    const until = Number(window.localStorage.getItem(SNOOZE_KEY)) || 0;
    return Math.min(until, Date.now() + NEW_BUILD_SNOOZE_MS);
  } catch {
    return 0;
  }
}

/** "Not now" from any tab, kept in step across all of them. */
function useSnooze(): [number, () => void] {
  const [until, setUntil] = useState(readSnoozedUntil);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === SNOOZE_KEY) setUntil(readSnoozedUntil());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const snooze = () => {
    const next = Date.now() + NEW_BUILD_SNOOZE_MS;
    try {
      window.localStorage.setItem(SNOOZE_KEY, String(next));
    } catch {
      // Private window or blocked storage: the snooze still holds for this tab.
    }
    setUntil(next);
  };

  return [until, snooze];
}

/** Whether `at` has passed, re-rendering when it does. */
function usePast(at: number | null): boolean {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (at === null) return;
    let timer: ReturnType<typeof setTimeout>;
    // Re-armed if it fires early, which a clock change can make it do.
    const arm = () => {
      const wait = at - Date.now();
      if (wait <= 0) setTick((n) => n + 1);
      else timer = setTimeout(arm, Math.min(wait, 2 ** 31 - 1));
    };
    if (at > Date.now()) arm();
    return () => clearTimeout(timer);
  }, [at]);

  return at !== null && Date.now() >= at;
}

/**
 * `BUTTON_INLINE` is the app's red, which is the one colour on an amber strip
 * that reads as the strip's own rather than as an action.
 */
const BUTTON_INLINE_AMBER =
  'inline-flex cursor-pointer items-center justify-center gap-1.5 bg-transparent text-sm text-amber-900 ' +
  'hover:underline';

/**
 * Both halves, drawn.
 *
 * One element for each app to place: iQD Pro puts it under the menu bar beside
 * the test-environment banner, the portal at the top of its own chrome. The
 * dialog is fixed to the viewport wherever this sits.
 */
export function NewBuildNotice({
  pollMs,
  bannerAfterMs = NEW_BUILD_BANNER_AFTER_MS,
}: {
  pollMs?: number;
  bannerAfterMs?: number;
}) {
  const available = useNewBuildAvailable(pollMs);
  const staleChunk = useStaleChunkFailure();
  useReloadOnNavigation(available);

  const [detectedAt, setDetectedAt] = useState<number | null>(null);
  useEffect(() => {
    if (available) setDetectedAt((at) => at ?? Date.now());
  }, [available]);

  const [snoozedUntil, snooze] = useSnooze();
  const due = usePast(
    detectedAt === null ? null : Math.max(detectedAt + bannerAfterMs, snoozedUntil),
  );

  return (
    <>
      {due && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          {/* The warning is quieter than the news, because it is a caveat on
              the button rather than the reason for the strip. Only the
              estimator asks before a reload; other screens just lose it. */}
          <p>
            New version available. It loads when you next change screen.{' '}
            <span className="text-amber-900/70">
              Reloading now loses unsaved work on this screen.
            </span>
          </p>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <button type="button" className={BUTTON_PRIMARY} onClick={reload}>
              Reload
            </button>
            <button type="button" className={BUTTON_INLINE_AMBER} onClick={snooze}>
              Not now
            </button>
          </div>
        </div>
      )}

      <StaleBuildDialog open={staleChunk} />
    </>
  );
}

/**
 * What a tab that has already broken is told.
 *
 * Closable, and closing it changes nothing — the screen behind it is still
 * broken. It closes anyway so that somebody can get at a part-written note on
 * the page and copy it out before reloading.
 */
export function StaleBuildDialog({ open }: { open: boolean }) {
  const [dismissed, setDismissed] = useState(false);

  return (
    <DialogShell
      open={open && !dismissed}
      title="Reload to continue"
      onClose={() => setDismissed(true)}
      width="max-w-md"
      footer={
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-hilevel-border bg-hilevel-light/50 px-4 py-3 sm:px-6">
          <button type="button" className={BUTTON_SECONDARY} onClick={() => setDismissed(true)}>
            Not now
          </button>
          <button type="button" className={BUTTON_PRIMARY} onClick={reload}>
            Reload
          </button>
        </div>
      }
    >
      <p className="text-sm text-hilevel-slate">
        This screen could not be loaded. The app has been updated since this tab was opened, and the
        part it just asked for is no longer being served.
      </p>
      {/* Same caveat as the banner, in the same words. */}
      <p className="text-sm text-hilevel-gray">
        Reloading fixes it, and loses unsaved work on this screen.
      </p>
    </DialogShell>
  );
}
